import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { RHFDateTimePickerField } from '@openx/components-core/lib/DateTimePickerField/RHFDateTimePickerField';
import {
  type DealFormData,
  type DealParticipant,
  dealFormField,
  dealStatus,
  type PmpDealType,
  pmpDealType,
} from '@openx/types/deal';
import { BUDGET_MODE, type BudgetMode, PACING_MODE, type PacingMode } from '@openx/types/monetizing';
import { DEAL_SOURCE_DSP_SOURCED, DV360_DEMAND_PARTNER_ID } from '@openx/utils/services/config';
import { addMinutes } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { type JSX, useCallback } from 'react';
import { type UseFormSetValue, type UseFormTrigger, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type PacingFormDateProps = {
  startLabel: string;
  endLabel: string;
  setValue: UseFormSetValue<DealFormData>;
  trigger: UseFormTrigger<DealFormData>;
  isEdit: boolean;
  dealType?: PmpDealType | null;
  isPacingEnabled?: boolean;
  monetizingMode?: PacingMode | null;
  budgetMode?: BudgetMode | null;
  isStartDateOneHourAhead: boolean;
  isPacingHidden?: boolean;
};

export const PacingFormDate = ({
  startLabel,
  endLabel,
  setValue,
  trigger,
  isEdit,
  dealType,
  isPacingEnabled,
  monetizingMode,
  budgetMode,
  isStartDateOneHourAhead,
  isPacingHidden = false,
}: PacingFormDateProps): JSX.Element => {
  const { t } = useTranslation();
  const observableDemandPartner = useWatch<Pick<DealFormData, 'deal_participants'>>({
    name: dealFormField.DEAL_PARTICIPANTS,
  });
  const observableDealSource = useWatch<Pick<DealFormData, 'deal_source'>>({
    name: dealFormField.DEAL_SOURCE,
  });

  const hasDV360DemandPartner = (observableDemandPartner as DealParticipant[])?.some(
    participant => participant.demand_partner === DV360_DEMAND_PARTNER_ID
  );

  const isEndDateRequired =
    (hasDV360DemandPartner && observableDealSource !== DEAL_SOURCE_DSP_SOURCED) ||
    (isPacingEnabled
      ? monetizingMode === PACING_MODE.EVENLY &&
        (dealType === pmpDealType.Guaranteed || budgetMode === BUDGET_MODE.CAMPAIGN_FLIGHT_TIME)
      : false);

  const isStartDateDisabled = isEdit && isPacingEnabled && !isStartDateOneHourAhead;
  const oneHourFromUtcTime = addMinutes(new Date(), new Date().getTimezoneOffset() + 60);
  const currentUTCTime = addMinutes(new Date(), new Date().getTimezoneOffset());

  const handleDateChange = useCallback(
    (value: Date | null, dateType: typeof dealFormField.END_DATE | typeof dealFormField.START_DATE) => {
      setValue(dateType, value, { shouldDirty: true, shouldValidate: true });

      if (dateType === dealFormField.END_DATE) {
        const endDateInFuture = value && fromZonedTime(value, 'UTC') > new Date();

        if (endDateInFuture) {
          setValue(dealFormField.STATUS, dealStatus.Active, { shouldDirty: true, shouldValidate: true });
        }
      }

      trigger(dealFormField.START_DATE);
      trigger(dealFormField.END_DATE);
    },
    [trigger, setValue]
  );

  return (
    <Grid container>
      <Grid
        size={12}
        sx={{
          mb: 0.5,
          mt: 2,
        }}
      >
        <Typography variant="h3" data-test="pacing-duration-label">
          {t('Duration')}{' '}
          {!isPacingHidden && (
            <Tooltip
              title={'Date refers to both Deal Duration time and Pacing'}
              placement="right"
              data-test="pacing-duration-tooltip"
            >
              <IconButton size="small">
                <InfoRoundedIcon />
              </IconButton>
            </Tooltip>
          )}
        </Typography>
      </Grid>
      <Grid
        container
        spacing={1}
        sx={{
          width: '100%',
        }}
      >
        <Grid size={6}>
          <RHFDateTimePickerField
            name={dealFormField.START_DATE}
            label={startLabel}
            margin="dense"
            data-test="deal-start-date"
            onChange={value => handleDateChange(value, dealFormField.START_DATE)}
            disabled={isStartDateDisabled}
            minDateTime={isPacingEnabled ? oneHourFromUtcTime : currentUTCTime}
            showTodayButton
          />
        </Grid>
        <Grid size={6}>
          <RHFDateTimePickerField
            name={dealFormField.END_DATE}
            label={`${endLabel} ${isEndDateRequired ? '' : t('(Optional)')}`}
            clearable
            margin="dense"
            data-test="deal-end-date"
            onChange={value => handleDateChange(value, dealFormField.END_DATE)}
            minDateTime={oneHourFromUtcTime}
            showTodayButton
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
